import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllCategories,
  getSingleCategoryById,
  createCategory,
  updateCategory,
  deleteCategory,
} from "@/api/new-api/staff-management/menu-manage/essential-menu/category";

// Thunks

export const fetchAllCategories = createAsyncThunk(
  "categories/fetchAll",
  async ({ restaurant_id, menu_id }, { rejectWithValue }) => {
    const res = await getAllCategories(restaurant_id, menu_id);
    if (res.status === "success") return res.data.categories;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleCategory = createAsyncThunk(
  "categories/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleCategoryById(id);
    if (res.status === "success") return res.data.category;
    return rejectWithValue(res.message);
  }
);

export const createNewCategory = createAsyncThunk(
  "categories/create",
  async (data, { rejectWithValue }) => {
    const res = await createCategory(data);
    if (res.status === "success") return res.data.category;
    return rejectWithValue(res.message);
  }
);

export const updateExistingCategory = createAsyncThunk(
  "categories/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateCategory(id, data);
    if (res.status === "success") return res.data.category;
    return rejectWithValue(res.message);
  }
);

export const deleteCategoryById = createAsyncThunk(
  "categories/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteCategory(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  categories: [],
  singleCategory: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const categorySlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    clearCategoryMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllCategories.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllCategories.fulfilled, (state, action) => {
        state.getLoading = false;
        state.categories = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllCategories.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleCategory.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleCategory.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleCategory = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleCategory.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewCategory.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewCategory.fulfilled, (state, action) => {
        state.createLoading = false;
        state.categories.push(action.payload);
        state.success = "Category created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewCategory.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingCategory.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingCategory.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.categories = state.categories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        );
        state.success = "Category updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingCategory.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteCategoryById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteCategoryById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.categories = state.categories.map((category) =>
          category.id === action.payload ? { ...category, status: "archive" } : category
        );
        state.success = "Category deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteCategoryById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearCategoryMessages } = categorySlice.actions;
export default categorySlice.reducer;
